import { toast } from 'sonner';

/**
 * Custom hook that wraps sonner's toast API
 * Drop-in replacement for antd's message API
 *
 * Usage:
 * const { showSuccess, showError, showInfo, showWarning, showLoading } = useToast();
 *
 * showSuccess('Operation completed');
 * showError('Something went wrong');
 * showLoading('Processing...'); // Returns a toast ID for dismissing
 */
export function useToast() {
  const showSuccess = (message: string, description?: string) => {
    toast.success(message, { description });
  };

  const showError = (message: string, opts?: string | { description?: string; duration?: number }) => {
    const options = typeof opts === "string" ? { description: opts } : opts;
    toast.error(message, { description: options?.description, duration: options?.duration });
  };

  const showInfo = (message: string, description?: string) => {
    toast.info(message, { description });
  };

  const showWarning = (message: string, description?: string) => {
    toast.warning(message, { description });
  };

  const showLoading = (message: string) => {
    return toast.loading(message);
  };

  const dismiss = (toastId?: string | number) => {
    toast.dismiss(toastId);
  };

  const showPromise = <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    }
  ) => {
    return toast.promise(promise, messages);
  };

  return {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showLoading,
    dismiss,
    showPromise,
    // Direct access to toast for advanced usage
    toast,
  };
}

/**
 * Direct toast functions for use outside of React components
 * Matches antd's message API for easy migration
 *
 * Usage:
 * import { message } from '@/hooks/useToast';
 * message.success('Done!');
 * message.error('Failed!');
 */
export const message = {
  success: (content: string, duration?: number) => {
    toast.success(content, { duration: duration ? duration * 1000 : undefined });
  },
  error: (content: string, duration?: number) => {
    toast.error(content, { duration: duration ? duration * 1000 : undefined });
  },
  info: (content: string, duration?: number) => {
    toast.info(content, { duration: duration ? duration * 1000 : undefined });
  },
  warning: (content: string, duration?: number) => {
    toast.warning(content, { duration: duration ? duration * 1000 : undefined });
  },
  loading: (content: string) => {
    return toast.loading(content);
  },
  destroy: (toastId?: string | number) => {
    toast.dismiss(toastId);
  },
};

export default useToast;
